import React, { useState, useEffect } from "react";
import { StaticSelectInput } from "../../../../constant/SelectInput";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { editDocumentApi } from "../../../../../services/DocumentService";
import { documentTypeOptionsList, getLabelByValue } from "../../../../../utils";

const EditDocument = ({ userPermission }) => {
  const [dataInput, setDataInput] = useState({
    title: "",
    document_type: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const data = location.state?.data;

  useEffect(() => {
    if (data) {
      setDataInput({
        id: data.id || "",
        title: data.title || "",
        description: data.description || "",
        document_type: data.document_type || "",
      });
      setTags(data.tags);
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setDataInput((prevDataInput) => ({ ...prevDataInput, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setDataInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleTagInput = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      const newTag = tagInput.trim();
      const updatedTags = tags ? `${tags}, ${newTag}` : newTag;
      setTags(updatedTags);
      setTagInput("");
      setErrors((prevErrors) => ({
        ...prevErrors,
        tags: "",
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    const updatedTags = tags
      .split(", ")
      .filter((tag) => tag !== tagToRemove)
      .join(", ");
    setTags(updatedTags);
  };

  const validateInputs = () => {
    const validationErrors = {};
    if (!dataInput.title) validationErrors.title = "Document Title is required";
    if (!dataInput.description)
      validationErrors.description = "Description is required";
    if (!tags) validationErrors.tags = "At least one tag is required";
    return validationErrors;
  };

  const handleSubmit = async (isDraft) => {
    const validationErrors = validateInputs();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitLoading(true);
      const formData = new FormData();
      formData.append("id", dataInput.id);
      formData.append("title", dataInput.title);
      formData.append("document_type", dataInput.document_type);
      formData.append("description", dataInput.description);
      formData.append("tags", tags);
      formData.append("is_draft", isDraft);
      const response = await editDocumentApi(formData);
      toast.success(response.data.message);
      navigate(`/document/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.message);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setDataInput({
      title: "",
      document_type: "",
      description: "",
      document_data: "",
    });
    setErrors({});
    setTags("");
    navigate(`/document/${id}`);
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row px-sm-2 px-0">
          <div className="col-xl-12">
            <div className="card px-sm-3 px-1">
              <div className="card-body">
                <div className="d-flex align-items-center mb-4">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm btn-circle"
                    onClick={() => navigate(`/document/${id}`)}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                  </button>
                  <h3 className="mb-0 ms-2">Edit Document</h3>
                </div>
                <form>
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <StaticSelectInput
                          optionsList={documentTypeOptionsList}
                          setParentInputValue={handleSelectChange}
                          name="document_type"
                          value={dataInput.document_type}
                          isClearable
                          placeholder="Select Document Type"
                          label="Document Type"
                          isRequired={true}
                          defaultValue={{
                            label: getLabelByValue(data.document_type, documentTypeOptionsList),
                            value: data.document_type,
                          }}
                        />
                        {errors.document_type && (
                          <div className="text-danger">
                            {errors.document_type}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Title<span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          placeholder="Enter Title"
                          name="title"
                          value={dataInput.title}
                          onChange={handleChange}
                        />
                        {errors.title && (
                          <div className="text-danger">{errors.title}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Description<span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          placeholder="Enter Description"
                          name="description"
                          value={dataInput.description}
                          onChange={handleChange}
                        />
                        {errors.description && (
                          <div className="text-danger">
                            {errors.description}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center mb-2">
                        <label className="form-label me-3">
                          Tag<span className="text-danger">*</span>
                        </label>
                        <div>
                          {tags &&
                            tags.split(", ").map((tag, index) => (
                              <span
                                key={index}
                                className="badge px-2 py-0 bg-primary text-white me-2 mb-2"
                              >
                                {tag}
                                <span
                                  className="text-white ms-2"
                                  onClick={() => removeTag(tag)}
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </span>
                              </span>
                            ))}
                        </div>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add tags (Press Enter)"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagInput}
                      />
                      {errors.tags && (
                        <div className="text-danger mt-1">{errors.tags}</div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-start">
                    <button
                      type="button"
                      className="btn btn-primary me-3"
                      disabled={isSubmitLoading}
                      onClick={() => handleSubmit("No")}
                    >
                      {isSubmitLoading ? "Updating..." : "Update"}
                    </button>
                    {data.current_version?.status === "draft" && (
                      <button
                        type="button"
                        className="btn btn-secondary me-3"
                        onClick={() => handleSubmit("Yes")}
                        disabled={isSubmitLoading}
                      >
                        Save as Draft
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditDocument;
